import React, { Component } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
  Paper,
  Theme,
} from '@mui/material';

import { hasSealsAndSignaturesPermission } from '@paragon/login-tools';
import { enqueueWarning } from '@paragon/notification-tools';
import { Dialog } from '@paragon/popup-state';

import { rejectJob, resetJob, resetSeals, returnJob, sealJobDefaultSettings } from '../../actions/job';
import { showSealJobSpecialDialog } from '../../actions/popup';
import { getSortedAnnotationTemplates } from '../../selectors/annotationTemplates';
import { getMetaPdfsAndComponentsAsSortedArrays } from '../../selectors/documents';

const buttonSx = {
  my: 1,
  py: 1,
  width: '100%',
  bgcolor: 'backContrast.main',
  '&:hover': {
    bgcolor: 'primary.dark',
    color: '#fff',
  },
} as const;

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;

type Props = StateProps & DispatchProps;

interface State {
  expanded: boolean;
  resetJobOpen: boolean;
  resetSealsOpen: boolean;
  returnJobAgainOpen: boolean;
  returnJobResultOpen: boolean;
  hasBeenReturned: boolean;
}

class SealingTools extends Component<Props, State> {
  state = {
    expanded: true,
    resetJobOpen: false,
    resetSealsOpen: false,
    returnJobAgainOpen: false,
    returnJobResultOpen: false,
    hasBeenReturned: false,
  };

  onToggleExpansion = () => {
    this.setState((prevState) => ({ expanded: !prevState.expanded }));
  };

  onResetJobClick = () => {
    this.setState({ resetJobOpen: true });
  };

  onResetSealsClick = () => {
    this.setState({ resetSealsOpen: true });
  };

  onReturnJobClick = () => {
    if (!this.props.jobIsSealed) {
      this.props.enqueueWarning('You can only return a job after it has been sealed.');
      return;
    }

    if (this.state.hasBeenReturned) {
      this.setState({ returnJobAgainOpen: true });
      return;
    }

    this.returnJob();
  };

  onResetJobConfirmed = () => {
    this.props.resetJob();
    this.setState({ resetJobOpen: false });
  };

  onResetSealsConfirmed = () => {
    this.props.resetSeals();
    this.setState({ resetSealsOpen: false });
  };

  confirmReturnJob = () => {
    this.closeReturnJobAgainDialog();
    this.returnJob();
  };

  closeResetJobDialog = () => {
    this.setState({ resetJobOpen: false });
  };

  closeResetSealsDialog = () => {
    this.setState({ resetSealsOpen: false });
  };

  closeReturnJobAgainDialog = () => {
    this.setState({ returnJobAgainOpen: false });
  };

  closeReturnJobResultDialog = () => {
    this.setState({ returnJobResultOpen: false });
  };

  async returnJob() {
    if (await this.props.returnJob()) {
      this.setState({ hasBeenReturned: true, returnJobResultOpen: true });
    }
  }

  render() {
    return (
      <div>
        <Card sx={{ maxWidth: 400, 'z-index': 1, bgcolor: 'backContrast.dark' }}>
          <div onClick={this.onToggleExpansion}>
            <Paper>
              <CardActions sx={{ display: 'flex', height: 60 }} disableSpacing>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'primary.main' }} aria-label="Recipe">
                      <CheckCircleIcon sx={{ color: '#ffffff' }} />
                    </Avatar>
                  }
                  title="Sealing Tools"
                />
                <IconButton
                  sx={{
                    transform: this.state.expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: (theme: Theme) =>
                      theme.transitions.create('transform', {
                        duration: theme.transitions.duration.shortest,
                      }),
                    ml: 'auto',
                  }}
                  aria-expanded={this.state.expanded}
                  aria-label="Show more"
                >
                  <ExpandMoreIcon />
                </IconButton>
              </CardActions>
            </Paper>
          </div>
          <Collapse in={this.state.expanded} timeout="auto" unmountOnExit>
            <CardContent>
              <Box sx={{ textAlign: 'center' }}>
                {this.props.hasSealsAndSignaturesPermission && (
                  <>
                    <Button variant="outlined" sx={buttonSx} onClick={this.props.sealJobDefaultSettings}>
                      Seal Job
                    </Button>

                    <Button variant="outlined" sx={buttonSx} onClick={this.props.showSealJobSpecialDialog}>
                      Seal Job Special
                    </Button>
                  </>
                )}

                <Button variant="outlined" sx={buttonSx} onClick={this.onResetJobClick}>
                  Reset Job
                </Button>

                {this.props.hasSealsAndSignaturesPermission && (
                  <>
                    <Button variant="outlined" sx={buttonSx} onClick={this.onResetSealsClick}>
                      Reset Seals
                    </Button>

                    <Button variant="outlined" sx={buttonSx} onClick={this.onReturnJobClick}>
                      Return Sealed Drawings
                    </Button>

                    <Button variant="outlined" sx={buttonSx} onClick={this.props.rejectJob}>
                      Reject Drawings
                    </Button>
                  </>
                )}
              </Box>
            </CardContent>
          </Collapse>
        </Card>

        <Dialog
          open={this.state.resetJobOpen}
          submitText="Reset"
          onCancel="close"
          onSubmit={this.onResetJobConfirmed}
          onClose={this.closeResetJobDialog}
        >
          Are you sure you want to RESET this job?
        </Dialog>

        <Dialog
          open={this.state.resetSealsOpen}
          submitText="Reset"
          onCancel="close"
          onSubmit={this.onResetSealsConfirmed}
          onClose={this.closeResetSealsDialog}
        >
          Are you sure you want to RESET seals?
        </Dialog>

        <Dialog
          open={this.state.returnJobAgainOpen}
          submitText="Return"
          onCancel="close"
          onSubmit={this.confirmReturnJob}
          onClose={this.closeReturnJobAgainDialog}
        >
          This job has been returned. Return it again?
        </Dialog>

        <Dialog
          insistent
          submitText="Ok"
          open={this.state.returnJobResultOpen}
          onClose={this.closeReturnJobResultDialog}
        >
          {`The job will be returned to ${this.props.creatorEmail}. You can safely exit the job.`}
        </Dialog>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => {
  const { activeDocumentId } = state.documents;
  const selectedDocument = state.documents.pdfData[activeDocumentId];
  const notes = selectedDocument == null ? '' : selectedDocument.notes || '';
  const { jobId } = state.job;
  const visible = state.view.controlPanelVisibility.visibleSections.sealing;
  const [metaPdfs, components] = getMetaPdfsAndComponentsAsSortedArrays(state);

  return {
    activeDocumentId,
    notes,
    jobId,
    creatorEmail: state.job.creatorEmail,
    jobIsSealed: state.job.stampedByEmail != null,
    visible,
    templates: getSortedAnnotationTemplates(state),
    metaPdfs,
    components,
    selectedTemplate: state.annotationTemplates.annotationTemplates[state.annotationTemplates.selectedTemplate],
    hasSealsAndSignaturesPermission: hasSealsAndSignaturesPermission(state),
  };
};

const actionCreators = {
  sealJobDefaultSettings,
  returnJob,
  rejectJob,
  resetJob,
  resetSeals,
  showSealJobSpecialDialog,
  enqueueWarning,
};

export default connect(mapStateToProps, actionCreators)(SealingTools);
